import {defineStore} from "pinia";
import {computed, type ComputedRef, type Ref, ref} from "vue";
import {Source} from "@/domain/type/source.type.ts";
import SynchroApi from "@/api/synchro.api.ts";
import {SynchroType, SyncState} from "@/domain/type/synchro.type.ts";
import {Product} from "@/domain/entity/Product.ts";
import {User} from "@/domain/entity/User.ts";
import type {AbstractEntity} from "@/domain/entity/AbstractEntity.ts";
import {Course} from "@/domain/entity/Course.ts";
import type {GetEntityResponse} from "@/domain/type/synchro.api.type.ts";
import {Location} from "@/domain/entity/Location.ts";
import {Session} from "@/domain/entity/Session.ts";

export const useEntitiesStore = defineStore('entities', () => {
  const ownerId: Ref<string> = ref('')
  const isLoading: Ref<boolean> = ref(false)
  const search: Ref<string> = ref('')
  const sourceLeft: Ref<Source> = ref(Source.EVA)
  const sourceRight: Ref<Source> = ref(Source.AMMON)
  const entities: Ref<AbstractEntity[] | null> = ref(null)
  const entityType: Ref<string> = ref('')
  const stateFilter: Ref<SyncState | null> = ref(null)
  const filteredEntities: ComputedRef<AbstractEntity[]> = computed(() => {
    return filter(stateFilter.value);
  })

  const countAll: ComputedRef<number> = computed(() => {
    return filter(null).length
  });

  const countOk: ComputedRef<number> = computed(() => {
    return filter(SyncState.OK).length
  });

  const countMissing: ComputedRef<number> = computed(() => {
    return filter(SyncState.MISSING).length
  });

  const countDiff: ComputedRef<number> = computed(() => {
    return filter(SyncState.DIFF).length
  });

  const countTooMany: ComputedRef<number> = computed(() => {
    return filter(SyncState.TOO_MANY).length
  });

  const countOtherOwner: ComputedRef<number> = computed(() => {
    return filter(SyncState.OTHER_OWNER).length
  });

  function filter(stateFilter: SyncState | null) {
    if (entities.value === null) return []
    return entities.value.filter((entity) => {
      if (stateFilter) {
        if (entity.state !== stateFilter) {
          return false
        }
      }

      if (search.value.length < 2) {
        return true;
      }
      return entity.match(search.value.toLowerCase())
    })
  }

  async function linkToCompany(entityUuid: string) {
    return SynchroApi.linkEntityToOwner(entityUuid, ownerId.value).then(() => {
      return refreshEntity(entityUuid)
    });
  }

  async function create(entityUuid: string) {
    return SynchroApi.applyEntity(entityUuid).then(() => {
      return refreshEntity(entityUuid)
    });
  }

  async function getEntity(entityUuid: string) {
    let response = await SynchroApi.getEntity(entityUuid);
    if (null === entities.value) {
      return Promise.resolve()
    }
    const entity = response.data
    const entityInstance = matchEntity(entity)
    if (null !== entityInstance) {
      const index = entities.value.findIndex((u) => u.uuid === entityInstance.uuid)
      if (index !== -1) {
        entities.value[index] = entityInstance
      }
    }
    return Promise.resolve()
  }

  async function refreshEntity(entityUuid: string) {
    return SynchroApi.refreshEntity(entityUuid).then(() => {
      return getEntity(entityUuid);
    });

  }

  function matchEntity(entity: GetEntityResponse): null | AbstractEntity {
    switch (entity.requestType) {
      case SynchroType.PERSONS:
        return new User(
          entity.uuid,
          entity.entity,
          entity.matchingEntities,
          entity.state,
          entity.status
        )
      case SynchroType.PRODUCT:
        return new Product(
          entity.uuid,
          entity.entity,
          entity.matchingEntities,
          entity.state,
          entity.status
        )
      case SynchroType.COURSE:
        return new Course(
          entity.uuid,
          entity.entity,
          entity.matchingEntities,
          entity.state,
          entity.status
        )
      case SynchroType.LOCATION:
        return new Location(
          entity.uuid,
          entity.entity,
          entity.matchingEntities,
          entity.state,
          entity.status
        )
      case SynchroType.SESSIONS:
        return new Session(
          entity.uuid,
          entity.entity,
          entity.matchingEntities,
          entity.state,
          entity.status
        )
      default:
        console.warn(`Unknown entity type: ${entity.requestType}`)
        entityType.value = 'unknown'
        return null
    }
  }

  function fetchList(expectedCompanyId: string | null, type: string) {
    isLoading.value = true
    entities.value = null // Au lieu de []
    SynchroApi.demand(sourceLeft.value, sourceRight.value, type, expectedCompanyId).then((demandResponse) => {
      if (demandResponse.data.requestUuid) {
        SynchroApi.getRequest(demandResponse.data.requestUuid).then((getResponse) => {
          const newEntities: AbstractEntity[] = [];
          getResponse.data.entities.forEach((entity: GetEntityResponse) => {
            const matchingEntity = matchEntity(entity)
            if (null !== matchingEntity) {
              newEntities.push(matchingEntity);
            }
          })
          entities.value = newEntities
        });
      }
    }).finally(() => {
      isLoading.value = false
    });
  }

  return {
    ownerId,
    create,
    countAll,
    countMissing,
    countDiff,
    countTooMany,
    countOtherOwner,
    countOk,
    fetchList,
    filteredEntities,
    isLoading,
    linkToCompany,
    refreshEntity,
    search,
    sourceLeft,
    sourceRight,
    entities,
    entityType,
    stateFilter
  }
})
